import * as ACTIONS from '@/store/constants/actions.js';
import * as GETTERS from '@/store/constants/getters.js';
import * as MUTATIONS from '@/store/constants/mutations.js';
import {
    createWorkspace, deleteWorkspace, getWorkspaces, getWorkspace, renameWorkspace, reportEvent, copyWorkspace,
} from '@/generated/console.js';
import { formatFiltersFromApi } from '../../helpers/moderation_list.js';

const WORKSPACE_OPEN = 'workspace.open';

const commitWorkspace = (commit, getters, workspace) => {
    commit(MUTATIONS.SET_WORKSPACE, { workspace });

    workspace.queues.forEach((queue) => {
        commit(MUTATIONS.SET_QUEUE, {
            key: queue.queue_uuid,
            item: {
                ...queue,
                filters: formatFiltersFromApi(queue.filters, getters),
            },
        });
    });

    workspace.saved_searches.forEach((search) => {
        commit(MUTATIONS.SET_SEARCH, {
            key: search.saved_search_uuid,
            item: {
                ...search,
                filters: formatFiltersFromApi(search.filters, getters),
            },
        });
    });

    return Promise.resolve();
};

const fetchActiveUsers = (dispatch, workspace) => {
    const users = [ ...new Set(workspace.queues.flatMap(({ active_users }) => active_users).filter(Boolean)) ];
    users.map((actor_uuid) => dispatch(ACTIONS.FETCH_USER_BY_UUID_WITHOUT_SITE, {
        uuid: actor_uuid,
    }));

    return Promise.all(users);
};

export const actions = {
    [ACTIONS.FETCH_WORKSPACES]({ commit, getters, dispatch }) {
        return getWorkspaces().then((response) => {
            response.workspaces.forEach((workspace) => commitWorkspace(commit, getters, workspace)
                .then(fetchActiveUsers(dispatch, workspace)));
            return Promise.resolve();
        });
    },
    [ACTIONS.FETCH_WORKSPACES_INFORMATION]({ getters, commit, dispatch }) {
        return getters[GETTERS.ACTIVE_WORKSPACES].map(({ workspace_uuid }) => getWorkspace({ workspace_uuid }).then((workspace) => {
            workspace.queues.forEach((queue) => {
                commit(MUTATIONS.SET_QUEUE, {
                    key: queue.queue_uuid,
                    item: {
                        active_users: queue.active_users,
                        outstanding_items: queue.outstanding_items,
                        urgent_items: queue.urgent_items,
                    },
                });

                return fetchActiveUsers(dispatch, workspace);
            });
            return Promise.resolve();
        }));
    },
    [ACTIONS.FETCH_WORKSPACE]({ commit, getters, dispatch }, workspace_uuid) {
        return getWorkspace({
            workspace_uuid,
        })
            .then((workspace) => commitWorkspace(commit, getters, workspace)
                .then(fetchActiveUsers(dispatch, workspace)));
    },
    [ACTIONS.CREATE_WORKSPACE]({ commit }, workspace) {
        return createWorkspace({ workspace }).then((response) => {
            commit(MUTATIONS.SET_WORKSPACE, { workspace: response });
            return response.uuid;
        });
    },
    [ACTIONS.RENAME_WORKSPACE]({ commit }, { workspace_uuid, name }) {
        const params = {
            workspace_uuid,
            mutation: {
                patch: [ {
                    path: '/name',
                    op: 'replace',
                    value: name,
                } ],
            },
        };
        return renameWorkspace(params).then(() => {
            commit(MUTATIONS.SET_WORKSPACE_PROPERTY, {
                key: workspace_uuid,
                item: { name },
            });
            return Promise.resolve();
        });
    },
    [ACTIONS.SET_WORKSPACE_ACCESS]({ commit }, content_id) {
        const params = {
            event: {
                content_id,
                action: WORKSPACE_OPEN,
            },
        };
        return reportEvent(params).then(() => {
            commit(MUTATIONS.SET_WORKSPACE_PROPERTY, {
                key: content_id,
                item: { last_accessed: new Date() },
            });
            return Promise.resolve();
        });
    },
    [ACTIONS.DELETE_WORKSPACE]({ commit }, { workspace_uuid }) {
        return deleteWorkspace({ workspace_uuid }).then(() => {
            commit(MUTATIONS.CLEAR_ACTIVE_WORKSPACE, workspace_uuid);
            commit(MUTATIONS.REMOVE_WORKSPACE, workspace_uuid);
            return Promise.resolve();
        });
    },
    [ACTIONS.DUPLICATE_WORKSPACE]({ commit, getters }, { workspace_uuid }) {
        return copyWorkspace({ workspace_uuid })
            .then((newWorkspace) => {
                commitWorkspace(commit, getters, newWorkspace);

                // copy all the workspace numbers over so the backend doesn't have to do it
                newWorkspace.queues.forEach((newQueue) => {
                    // Find the corresponding queue in the old workspace and then copy the values over
                    // We match queues by filters. This is fine because if the filters are identical, then the numbers will also be identical
                    const oldQueue = getters[GETTERS.QUEUE_BY_FILTER](newQueue.filters.map(({ filter_uuid, ...filter }) => filter)); // eslint-disable-line no-unused-vars

                    if (oldQueue) {
                        commit(MUTATIONS.SET_QUEUE, {
                            key: newQueue.queue_uuid,
                            item: {
                                active_users: oldQueue.active_users,
                                outstanding_items: oldQueue.outstanding_items,
                                urgent_items: oldQueue.urgent_items,
                            },
                        });
                    }
                });
            });
    },
};
